import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2 } from "../icons";
import { cx } from "../ui";
import {
  computeCompleteness,
  pgaBadgeTone,
  resolvePgaFlagSchema,
  type PgaCode,
} from "../lib/pga-schema";
import type { AppliedPgaFlag } from "../lib/pga-applied";
import {
  fetchImportPgaFlagDefinition,
  type ImportPgaFlagDefinition,
} from "../lib/import-service";

const TONE_CLS: Record<"info" | "warning" | "danger", string> = {
  info: "ih-badge-info",
  warning: "ih-badge-warning",
  danger: "ih-badge-danger",
};

export const PgaFlagChip: React.FC<{
  code: PgaCode;
  flag?: AppliedPgaFlag;
  onClick: () => void;
}> = ({
  code,
  flag,
  onClick,
}) => {
  const [definition, setDefinition] = useState<ImportPgaFlagDefinition | null>(
    null,
  );
  useEffect(() => {
    let cancelled = false;
    fetchImportPgaFlagDefinition(code).then((result) => {
      if (!cancelled && result.success && result.definition) {
        setDefinition(result.definition);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code]);
  const schema = useMemo(
    () => resolvePgaFlagSchema(code, definition),
    [code, definition],
  );
  const values = flag?.values ?? {};
  const repeaters = flag?.repeaters ?? {};
  const { filled, total, requiredMissing } = computeCompleteness(schema, values, repeaters);
  const pct = total ? Math.min(100, Math.round((filled / total) * 100)) : 0;
  const tone = pgaBadgeTone(code);
  const complete = requiredMissing === 0 && filled > 0;
  const fillTone = complete ? "success" : requiredMissing > 0 ? "warning" : "info";
  return (
    <button type="button" onClick={onClick} className="ih-pga-chip">
      <div className="ih-pga-chip-head">
        <span className={cx("ih-badge", TONE_CLS[tone])}>
          {code.replace("_", " ")}
        </span>
        {complete ? (
          <CheckCircle2 size={14} className="ih-tone-success" />
        ) : requiredMissing > 0 ? (
          <AlertCircle size={14} className="ih-tone-warning" />
        ) : null}
      </div>
      <div className="ih-pga-chip-agency ih-truncate">{schema.agency}</div>
      <div className="ih-pga-chip-bar">
        <div className="ih-pga-chip-bar-track">
          <div className={cx("ih-pga-chip-bar-fill", fillTone)} style={{ width: `${pct}%` }} />
        </div>
        <span className="ih-pga-chip-count ih-num">{filled}/{total}</span>
      </div>
    </button>
  );
}
